import { JSX, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { graphqlRequest } from 'lib/helpers'
import { buildAppSyncWsClient, WEBSOCKET_SUB_QUERY } from 'lib/appSyncSubscription'
import { ResolvedMerchStyle, resolveMerchStyle } from 'lib/merchStyle'
import {
  MerchPctFormat,
  MerchTweenHandle,
  formatMerchTweenPct,
  merchTweenMoney,
  parseMerchPctLabel,
  startMerchTween
} from 'lib/merchTween'
import { MerchBoardPanel, MerchExtUiPreview, MerchRowVariant } from 'components/MerchBoardPanel'
import { MerchTickerEnvelopeMessage, MerchTickerMarquee, merchDebugTrace } from 'components/MerchTickerMarquee'
import {
  MERCH_TICKER_LABEL_COLORS,
  MerchTickerLabelColor,
  MerchTickerStyleDraft,
  resolveTickerStyleDraft
} from 'lib/merchTickerConfig'
import { useFontGate } from 'lib/fontLoader'
import { MerchBoardData } from 'types/api'
import getMerchBoardDataQuery from 'graphql/getMerchBoardDataById.graphql'
import logger from 'logger'

const log = logger.extend('merch-overlay')

const BOOTSTRAP_RETRY_MS = 15000

type BootstrapTrigger = 'mount' | 'retry' | 'connected'

const MAX_EXTENSION_DELAY_MS = 60000

const SPENT_CLASS_BY_ANIMATION: Readonly<Record<string, string>> = {
  'merch-flash-up': 'flash-up',
  'merch-flash-down': 'flash-down',
  'merch-blink': 'blink'
}

const onRowAnimationEnd = (e: AnimationEvent): void => {
  const spent = SPENT_CLASS_BY_ANIMATION[e.animationName]
  if (spent === undefined) return
  const row = e.currentTarget
  if (row instanceof HTMLElement) row.classList.remove(spent)
}

interface RunningPhase { name: string, currentTime: number }
interface CapturedPhases { at: number, phases: Map<string, RunningPhase[]> }
const captureRunningPhases = (rows: Map<string, HTMLElement>): CapturedPhases => {
  const phases = new Map<string, RunningPhase[]>()
  for (const [id, el] of rows) {
    const running: RunningPhase[] = []
    for (const a of el.getAnimations({ subtree: true })) {
      const name = (a as CSSAnimation).animationName
      if (typeof name !== 'string' || SPENT_CLASS_BY_ANIMATION[name] === undefined) continue
      if (typeof a.currentTime !== 'number') continue
      running.push({ name, currentTime: a.currentTime })
    }
    if (running.length > 0) phases.set(id, running)
  }
  return { at: performance.now(), phases }
}

interface BoardRowTween {
  handle: MerchTweenHandle
  priceIdx: number
  pctIdx: number
}
type BoardTweens = Map<string, BoardRowTween>

interface BoardPrevValue {
  priceCents: number | null
  pct: number | null
  pctLabel: string | null
}

const stopBoardTweens = (tweens: BoardTweens, snap: boolean): void => {
  for (const t of tweens.values()) t.handle.stop(snap)
  tweens.clear()
}

const captureBoardPrevValues = (current: MerchEnvelope | null, tweens: BoardTweens): Map<string, BoardPrevValue> => {
  const out = new Map<string, BoardPrevValue>()
  if (current === null) return out
  for (const p of current.products) {
    const parsed = parseMerchPctLabel(p.pctLabel)
    out.set(p.productId, {
      priceCents: p.priceCents,
      pct: parsed === null ? null : parsed.value,
      pctLabel: p.pctLabel
    })
  }
  for (const [id, t] of tweens) {
    const prev = out.get(id)
    if (prev === undefined) continue
    if (t.priceIdx >= 0) prev.priceCents = Math.round(t.handle.values[t.priceIdx])
    if (t.pctIdx >= 0) prev.pct = t.handle.values[t.pctIdx]
  }
  return out
}

const startBoardRowTween = (
  tweens: BoardTweens,
  productId: string,
  row: HTMLElement,
  prev: BoardPrevValue,
  product: MerchEnvelopeProduct,
  durationMs: number
): void => {
  const from: number[] = []
  const to: number[] = []
  let priceIdx = -1
  let pctIdx = -1
  const priceTo = product.priceCents
  if (prev.priceCents !== null && priceTo !== null && prev.priceCents !== priceTo) {
    priceIdx = from.length
    from.push(prev.priceCents)
    to.push(priceTo)
  }
  const pctFmt: MerchPctFormat | null = parseMerchPctLabel(product.pctLabel)
  const pctTarget = product.pctLabel ?? ''
  if (prev.pct !== null && pctFmt !== null && prev.pct !== pctFmt.value) {
    pctIdx = from.length
    from.push(prev.pct)
    to.push(pctFmt.value)
  }
  if (from.length === 0) return
  const pctBudget = Math.max((prev.pctLabel ?? '').length, pctTarget.length)
  const write = (values: number[], done: boolean): void => {
    if (priceIdx >= 0) {
      const el = row.querySelector('.price')
      if (el !== null && priceTo !== null) {
        el.textContent = done ? merchTweenMoney(priceTo) : merchTweenMoney(Math.round(values[priceIdx]))
      }
    }
    if (pctIdx >= 0 && pctFmt !== null) {
      const el = row.querySelector('.pct')
      if (el !== null) {
        el.textContent = done ? pctTarget : formatMerchTweenPct(values[pctIdx], pctFmt, pctBudget, pctTarget)
      }
    }
  }
  write(from, false)
  const handle = startMerchTween(from, to, durationMs, write)
  if (handle.finished) return
  tweens.set(productId, { handle, priceIdx, pctIdx })
}

export interface MerchEnvelopeProduct {
  productId: string
  title: string
  priceCents: number | null
  imageUrl: string | null
  linkUrl: string | null
  variants: MerchRowVariant[]
  netUnits: number
  unitsSold: number
  unitsRefunded: number
  trend: string | null
  basePriceCents: number | null
  pctLabel: string | null
}

export interface MerchEnvelope {
  kind: string
  boardId: string
  version: number
  storeTitle: string
  mode: string
  cartBase: string | null
  products: MerchEnvelopeProduct[]
  deltas: Record<string, number>
  style: ResolvedMerchStyle
  displayStyle: string
  messages: MerchTickerEnvelopeMessage[]
  tickerStyle: MerchTickerStyleDraft
  extensionVisible: boolean
  extensionStartCollapsed: boolean
  extensionStartWidthPct: number | null
  shopMode: boolean
  shopLabelText: string
  ctaText: string | null
  origin: string | null
  extensionDelayMs: number
  updatedAt: string
}

const parseDirection = (direction: unknown, trend: unknown): string | null => {
  if (direction === 'up' || direction === 'down') return direction
  if (direction === 'flat') return null
  if (trend === 'up' || trend === 'down') return trend
  return null
}

const parseEnvelope = (raw: unknown): MerchEnvelope | null => {
  let obj: any = raw
  for (let unwraps = 0; typeof obj === 'string' && unwraps < 2; unwraps++) {
    try {
      obj = JSON.parse(obj)
    } catch {
      return null
    }
  }
  if (obj === null || typeof obj !== 'object') return null
  if (obj.kind !== 'MERCH' || typeof obj.boardId !== 'string') return null
  if (!Array.isArray(obj.products)) return null
  const mode = obj.mode === 'prices' ? 'prices' : 'orders'
  const products: MerchEnvelopeProduct[] = []
  for (const p of obj.products) {
    if (p === null || typeof p !== 'object' || typeof p.productId !== 'string') continue
    const variants: MerchRowVariant[] = []
    if (Array.isArray(p.variants)) {
      for (const v of p.variants) {
        if (v !== null && typeof v === 'object' && typeof v.id === 'string' && typeof v.label === 'string') {
          variants.push({
            id: v.id,
            label: v.label,
            linkUrl: typeof v.linkUrl === 'string' && v.linkUrl !== '' ? v.linkUrl : null
          })
        }
      }
    }
    products.push({
      productId: p.productId,
      title: typeof p.title === 'string' ? p.title : '',
      priceCents: typeof p.priceCents === 'number' ? p.priceCents : null,
      imageUrl: typeof p.featuredImage === 'string'
        ? p.featuredImage
        : (typeof p.imageUrl === 'string' ? p.imageUrl : null),
      linkUrl: typeof p.linkUrl === 'string' ? p.linkUrl : null,
      variants,
      netUnits: typeof p.netUnits === 'number' ? p.netUnits : 0,
      unitsSold: typeof p.unitsSold === 'number' ? p.unitsSold : 0,
      unitsRefunded: typeof p.unitsRefunded === 'number' ? p.unitsRefunded : 0,
      trend: parseDirection(p.direction, p.trend),
      basePriceCents: typeof p.basePriceCents === 'number' ? p.basePriceCents : null,
      pctLabel: mode === 'prices' && typeof p.pctLabel === 'string' && p.pctLabel !== ''
        ? p.pctLabel
        : null
    })
  }
  const deltas: Record<string, number> = {}
  if (obj.deltas !== null && typeof obj.deltas === 'object') {
    for (const [id, qty] of Object.entries(obj.deltas)) {
      if (typeof qty === 'number') deltas[id] = qty
    }
  }
  const messages: MerchTickerEnvelopeMessage[] = []
  if (Array.isArray(obj.messages)) {
    for (const m of obj.messages) {
      if (m === null || typeof m !== 'object') continue
      if (typeof m.id !== 'string' || typeof m.label !== 'string' || typeof m.text !== 'string') continue
      messages.push({
        id: m.id,
        label: m.label,
        labelColor: MERCH_TICKER_LABEL_COLORS.includes(m.labelColor as MerchTickerLabelColor)
          ? m.labelColor as MerchTickerLabelColor
          : 'green',
        text: m.text
      })
    }
  }
  return {
    kind: 'MERCH',
    boardId: obj.boardId,
    version: typeof obj.version === 'number' && Number.isFinite(obj.version) ? obj.version : 0,
    storeTitle: typeof obj.storeTitle === 'string' ? obj.storeTitle : '',
    mode,
    cartBase: typeof obj.cartBase === 'string' && obj.cartBase !== ''
      ? obj.cartBase
      : null,
    products,
    deltas,
    style: resolveMerchStyle(obj.style),
    displayStyle: obj.displayStyle === 'ticker' ? 'ticker' : 'board',
    messages,
    tickerStyle: resolveTickerStyleDraft(obj.tickerStyle),
    extensionVisible: obj.extensionVisible !== false,
    shopMode: obj.shopMode === true,
    shopLabelText: typeof obj.shopLabelText === 'string' ? obj.shopLabelText : '',
    ctaText: typeof obj.ctaText === 'string' && obj.ctaText !== '' ? obj.ctaText : null,
    extensionStartCollapsed: obj.extensionStartCollapsed === true,
    extensionStartWidthPct: typeof obj.extensionStartWidthPct === 'number' &&
      Number.isFinite(obj.extensionStartWidthPct) &&
      obj.extensionStartWidthPct >= 5 &&
      obj.extensionStartWidthPct <= 60
      ? obj.extensionStartWidthPct
      : null,
    origin: typeof obj.origin === 'string' ? obj.origin : null,
    extensionDelayMs: typeof obj.extensionDelayMs === 'number' &&
      Number.isInteger(obj.extensionDelayMs) &&
      obj.extensionDelayMs >= 0 &&
      obj.extensionDelayMs <= MAX_EXTENSION_DELAY_MS
      ? obj.extensionDelayMs
      : 0,
    updatedAt: typeof obj.updatedAt === 'string' ? obj.updatedAt : ''
  }
}

export const MerchBoardView = ({
  merchOverlayId,
  token,
  slug,
  natural,
  wrapWidth,
  onEnvelope,
  extui
}: {
  merchOverlayId: string
  token: string
  slug?: string
  natural?: boolean
  wrapWidth?: number
  onEnvelope?: (envelope: MerchEnvelope) => void
  extui?: MerchExtUiPreview
}): JSX.Element | null => {
  const [envelope, setEnvelope] = useState<MerchEnvelope | null>(null)
  const [channel, setChannel] = useState<string | null>(null)

  const envelopeRef = useRef<MerchEnvelope | null>(null)

  const onEnvelopeRef = useRef(onEnvelope)
  onEnvelopeRef.current = onEnvelope
  useEffect(() => {
    if (envelope !== null) onEnvelopeRef.current?.(envelope)
  }, [envelope])

  const rowRefs = useRef(new Map<string, HTMLElement>())
  const tickerRef = useRef<HTMLDivElement | null>(null)
  const styleBlock = envelope === null ? null : envelope.style
  const isTicker = envelope !== null && envelope.displayStyle === 'ticker'
  const titleFontReady = useFontGate(
    envelope === null
      ? null
      : (isTicker ? envelope.tickerStyle.fontKey : envelope.style.titleFontKey)
  )
  const nameFontReady = useFontGate(styleBlock === null || isTicker ? null : styleBlock.productTitleFontKey)
  const priceFontReady = useFontGate(styleBlock === null || isTicker ? null : styleBlock.priceFontKey)
  const fontsReady = titleFontReady && nameFontReady && priceFontReady

  const pendingFlipRef = useRef<{
    tops: Map<string, number>
    deltas: Record<string, number>
    prev: Map<string, BoardPrevValue>
  } | null>(null)
  const tweensRef = useRef<BoardTweens>(new Map())
  const pendingPhasesRef = useRef<CapturedPhases | null>(null)
  const loadRef = useRef<() => void>(() => {})

  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof globalThis.setTimeout> | undefined
    const load = (trigger: BootstrapTrigger): void => {
      graphqlRequest<{ merchBoard: MerchBoardData | null }>(
        getMerchBoardDataQuery,
        slug !== undefined ? { merchOverlayId, slug } : { merchOverlayId },
        token
      )
        .then((res) => {
          if (cancelled) return
          if (res.merchBoard === null) {
            log({ bootstrap: 'null merchBoard', merchOverlayId, viaSlug: slug !== undefined })
            return
          }
          const parsed = parseEnvelope(res.merchBoard.board)
          if (parsed === null) {
            log({
              bootstrap: 'unparseable board envelope',
              rawType: typeof res.merchBoard.board,
              rawHead: JSON.stringify(res.merchBoard.board).slice(0, 300)
            })
            return
          }
          const current = envelopeRef.current
          const trace = {
            path: 'bootstrap',
            trigger,
            version: parsed.version,
            current: current === null ? null : current.version,
            origin: parsed.origin,
            updatedAt: parsed.updatedAt,
            deltaKeys: Object.keys(parsed.deltas),
            displayStyle: parsed.displayStyle
          }
          if (current !== null && current.boardId === parsed.boardId && parsed.version <= current.version) {
            merchDebugTrace({ ...trace, verdict: 'dropped', reason: parsed.version === current.version ? 'duplicate' : 'stale' })
          } else {
            pendingFlipRef.current = null
            pendingPhasesRef.current = captureRunningPhases(rowRefs.current)
            stopBoardTweens(tweensRef.current, true)
            envelopeRef.current = parsed
            setEnvelope(parsed)
            merchDebugTrace({ ...trace, verdict: 'applied' })
          }
          setChannel(res.merchBoard.channel)
        })
        .catch((err: unknown) => {
          log({ bootstrap: 'failed', err: err instanceof Error ? err.name : 'unknown' })
          if (!cancelled) retryTimer = globalThis.setTimeout(() => load('retry'), BOOTSTRAP_RETRY_MS)
        })
    }
    loadRef.current = () => load('connected')
    load('mount')
    return () => {
      cancelled = true
      globalThis.clearTimeout(retryTimer)
    }
  }, [merchOverlayId, token, slug])

  useEffect(() => {
    if (channel === null || channel === '' || token === '') return
    const client = buildAppSyncWsClient(token)
    const offConnected = client.on('connected', () => { loadRef.current() })
    const unsubscribe = client.subscribe(
      { query: WEBSOCKET_SUB_QUERY, variables: { channel } },
      {
        next: (data: any) => {
          const dataField = data?.WebsocketSub?.data
          if (dataField == null) return
          const parsed = parseEnvelope(dataField)
          if (parsed === null) return
          const current = envelopeRef.current
          const trace = {
            path: 'push',
            version: parsed.version,
            current: current === null ? null : current.version,
            origin: parsed.origin,
            updatedAt: parsed.updatedAt,
            deltaKeys: Object.keys(parsed.deltas),
            displayStyle: parsed.displayStyle,
            changeAnimation: parsed.displayStyle === 'ticker' ? parsed.tickerStyle.changeAnimation : parsed.style.changeAnimation,
            extensionVisible: parsed.extensionVisible,
            extensionDelayMs: parsed.extensionDelayMs
          }
          if (parsed.boardId !== merchOverlayId) {
            merchDebugTrace({ ...trace, verdict: 'dropped', reason: 'board', boardId: parsed.boardId })
            return
          }
          if (current !== null && current.boardId === parsed.boardId && parsed.version <= current.version) {
            merchDebugTrace({ ...trace, verdict: 'dropped', reason: parsed.version === current.version ? 'duplicate' : 'stale' })
            return
          }
          const tops = new Map<string, number>()
          for (const [id, el] of rowRefs.current) {
            tops.set(id, el.getBoundingClientRect().top)
          }
          const prev = captureBoardPrevValues(current, tweensRef.current)
          stopBoardTweens(tweensRef.current, true)
          pendingFlipRef.current = { tops, deltas: parsed.deltas, prev }
          pendingPhasesRef.current = captureRunningPhases(rowRefs.current)
          envelopeRef.current = parsed
          setEnvelope(parsed)
          merchDebugTrace({ ...trace, verdict: 'applied' })
        },
        error: (err: unknown) => {
          log({ subscription: 'error', err: err instanceof Error ? err.name : 'unknown' })
        },
        complete: () => { }
      }
    )
    return () => {
      offConnected()
      unsubscribe()
      client.dispose()
    }
  }, [channel, token, merchOverlayId])

  useLayoutEffect(() => {
    const pending = pendingFlipRef.current
    pendingFlipRef.current = null
    const captured = pendingPhasesRef.current
    pendingPhasesRef.current = null
    if (envelope === null) return
    if (envelope.displayStyle === 'ticker') return
    if (captured !== null && captured.phases.size > 0) {
      const reanimated = pending === null ? {} : pending.deltas
      const elapsed = performance.now() - captured.at
      for (const [id, phases] of captured.phases) {
        if (Object.prototype.hasOwnProperty.call(reanimated, id)) continue
        const el = rowRefs.current.get(id)
        if (el === undefined) continue
        const live = el.getAnimations({ subtree: true })
        for (const phase of phases) {
          const a = live.find((x) => (x as CSSAnimation).animationName === phase.name)
          if (a === undefined || typeof a.currentTime !== 'number' || a.currentTime >= phase.currentTime) continue
          a.currentTime = phase.currentTime + elapsed
        }
      }
    }
    if (pending === null) return
    const mode = envelope.style.changeAnimation
    const deltaIds = Object.keys(pending.deltas)
    if (document.hidden) {
      merchDebugTrace({ path: 'board-pulse', verdict: 'skipped', reason: 'hidden', mode, ids: deltaIds, version: envelope.version })
      return
    }
    merchDebugTrace(envelope.shopMode || mode === 'NONE'
      ? { path: 'board-pulse', verdict: 'skipped', reason: envelope.shopMode ? 'shop-mode' : 'NONE', mode, ids: deltaIds, version: envelope.version }
      : { path: 'board-pulse', verdict: 'fired', mode, ids: deltaIds, version: envelope.version })
    for (const p of envelope.products) {
      const el = rowRefs.current.get(p.productId)
      if (el === undefined) continue
      const oldTop = pending.tops.get(p.productId)
      if (oldTop === undefined) continue
      const rect = el.getBoundingClientRect()
      const delta = oldTop - rect.top
      const ratio = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1
      const ancestorScale = Math.abs(ratio - 1) <= 0.01 ? 1 : ratio
      if (delta !== 0) {
        el.style.transition = 'none'
        el.style.transform = `translateY(${delta / ancestorScale}px)`
        void el.offsetHeight
        el.style.transition = ''
        el.style.transform = ''
      }
    }
    if (envelope.shopMode) {
      for (const el of rowRefs.current.values()) {
        el.classList.remove('flash-up', 'flash-down', 'blink')
      }
      return
    }
    if (mode === 'TWEEN') {
      for (const el of rowRefs.current.values()) {
        el.classList.remove('flash-up', 'flash-down', 'blink')
      }
      const byId = new Map(envelope.products.map((p) => [p.productId, p]))
      for (const id of deltaIds) {
        const el = rowRefs.current.get(id)
        const product = byId.get(id)
        const prev = pending.prev.get(id)
        if (el === undefined || product === undefined || prev === undefined) continue
        startBoardRowTween(tweensRef.current, id, el, prev, product, envelope.style.tweenMs)
      }
      return
    }
    if (mode === 'NONE') {
      for (const el of rowRefs.current.values()) {
        el.classList.remove('flash-up', 'flash-down', 'blink')
      }
      return
    }
    if (mode === 'BLINK') {
      for (const id of Object.keys(pending.deltas)) {
        const el = rowRefs.current.get(id)
        if (el === undefined) continue
        el.classList.remove('blink', 'flash-up', 'flash-down')
        void el.offsetWidth
        el.classList.add('blink')
      }
      return
    }
    for (const [id, qty] of Object.entries(pending.deltas)) {
      const el = rowRefs.current.get(id)
      if (el === undefined) continue
      el.classList.remove('flash-up', 'flash-down', 'blink')
      void el.offsetWidth
      el.classList.add(qty > 0 ? 'flash-up' : 'flash-down')
    }
  }, [envelope])

  useEffect(() => {
    const onVisibilityReturn = (): void => {
      if (document.hidden) return
      stopBoardTweens(tweensRef.current, true)
      for (const el of rowRefs.current.values()) {
        el.classList.remove('flash-up', 'flash-down', 'blink')
        el.style.transition = 'none'
        el.style.transform = ''
        void el.offsetHeight
        el.style.transition = ''
      }
    }
    document.addEventListener('visibilitychange', onVisibilityReturn)
    const tweens = tweensRef.current
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityReturn)
      stopBoardTweens(tweens, true)
    }
  }, [])

  useLayoutEffect(() => {
    if (natural === true) return
    const fit = (): void => {
      const ticker = tickerRef.current
      if (ticker === null) return
      ticker.style.transform = ''
      const available = globalThis.innerHeight
      if (available <= 0) return
      const needed = ticker.getBoundingClientRect().height + 48
      if (needed <= 0) return
      const scale = Math.min(1, available / needed)
      if (scale < 1) {
        ticker.style.transformOrigin = 'top right'
        ticker.style.transform = `scale(${scale})`
      }
    }
    fit()
    globalThis.addEventListener('resize', fit)
    void document.fonts?.ready?.then(() => fit())
    return () => globalThis.removeEventListener('resize', fit)
  }, [envelope, natural])

  if (envelope === null || !fontsReady) return null

  if (envelope.displayStyle === 'ticker') {
    return <MerchTickerMarquee envelope={envelope} wrapWidth={wrapWidth} />
  }

  return (
    <MerchBoardPanel
      storeTitle={envelope.storeTitle}
      rows={envelope.products}
      style={envelope.style}
      cartBase={envelope.cartBase}
      shopMode={envelope.shopMode}
      shopLabel={envelope.shopLabelText}
      ctaText={envelope.ctaText}
      natural={natural}
      wrapWidth={wrapWidth}
      extui={extui}
      tickerRef={tickerRef}
      rowRef={(productId, el) => {
        if (el === null) {
          rowRefs.current.get(productId)?.removeEventListener('animationend', onRowAnimationEnd)
          rowRefs.current.delete(productId)
        } else {
          rowRefs.current.set(productId, el)
          el.addEventListener('animationend', onRowAnimationEnd)
        }
      }}
    />
  )
}
